/**
 * Self-contained context that manages the cart flyout open/close state.
 * Any component in the tree can open the flyout via `useCartFlyoutContext().onOpen`.
 */
import {createContext, useCallback, useContext, useMemo, useState} from 'react';
import PropTypes from 'prop-types';

import {useDisclosure} from '@salesforce/retail-react-app/app/components/shared/ui';

const CartFlyoutContext = createContext(null);

export const CartFlyoutProvider = ({children}) => {
  const {isOpen, onOpen, onClose, onToggle} = useDisclosure();
  const [allowOnCheckout, setAllowOnCheckout] = useState(false);

  const handleClose = useCallback(() => {
    setAllowOnCheckout(false);
    onClose();
  }, [onClose]);

  /** Opens the flyout on checkout only after the shopper removed all line items there. */
  const openEmptyCartOnCheckout = useCallback(() => {
    setAllowOnCheckout(true);
    onOpen();
  }, [onOpen]);

  const value = useMemo(
    () => ({
      allowOnCheckout,
      isOpen,
      onClose: handleClose,
      onOpen,
      onToggle,
      openEmptyCartOnCheckout,
    }),
    [allowOnCheckout, isOpen, handleClose, onOpen, onToggle, openEmptyCartOnCheckout]
  );

  return <CartFlyoutContext.Provider value={value}>{children}</CartFlyoutContext.Provider>;
};

CartFlyoutProvider.displayName = 'CartFlyoutProvider';
CartFlyoutProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/** @throws {Error} when used outside CartFlyoutProvider */
export const useCartFlyoutContext = () => {
  const ctx = useContext(CartFlyoutContext);

  if (!ctx) {
    throw new Error('useCartFlyoutContext must be used within CartFlyoutProvider');
  }

  return ctx;
};

/**
 * @deprecated Use `useCartFlyoutContext` instead.
 */
export const useCartFlyout = useCartFlyoutContext;
